/**
 * BookingSheet — Step 1 of the booking flow.
 *
 * Collects date, time, location/virtual for a service, then hands off to the
 * existing PaymentSheet. On successful payment, creates a booking record with
 * funds parked in seller's pending (escrow).
 */
import { useMemo, useState } from "react";
import { X, Calendar, Clock, MapPin, Globe, ShieldCheck } from "lucide-react";
import PaymentSheet from "@/components/PaymentSheet";
import { useAuth } from "@/context/AuthContext";
import { createBooking } from "@/lib/bookingStore";

const todayISO = () => new Date().toISOString().slice(0, 10);
const ymdPlus = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

const TIME_SLOTS = [
    "09:00", "10:00", "11:00", "12:00",
    "13:00", "14:00", "15:00", "16:00",
    "17:00", "18:00", "19:00", "20:00",
];

const BookingSheet = ({ open, onClose, service, onBooked }) => {
    const { user } = useAuth();
    const serviceType = service?.serviceType || "in_person";
    const defaultMode = serviceType === "online" ? "virtual" : "in_person";

    const [date, setDate] = useState(ymdPlus(1));
    const [time, setTime] = useState("");
    const [mode, setMode] = useState(defaultMode);
    const [location, setLocation] = useState(service?.location || "");
    const [showPay, setShowPay] = useState(false);
    const [err, setErr] = useState(null);

    const amt = Number(service?.price ?? service?.priceFrom ?? 0);

    const canProceed = useMemo(() => {
        if (!date || !time) return false;
        if (mode === "in_person" && !location.trim()) return false;
        if (!amt) return false;
        return true;
    }, [date, time, mode, location, amt]);

    const allowVirtual = serviceType === "online" || serviceType === "hybrid";
    const allowInPerson = serviceType === "in_person" || serviceType === "hybrid";

    if (!open) return null;

    const handleProceed = () => {
        if (!canProceed) {
            setErr("Fill in date, time and location.");
            return;
        }
        setErr(null);
        setShowPay(true);
    };

    const handlePaymentSuccess = ({ method, reference }) => {
        const booking = createBooking({
            service,
            buyer: user,
            seller: {
                id: service?.ownerId,
                fullName: service?.providerName,
                username: service?.providerUsername,
            },
            date,
            time,
            mode,
            location: mode === "virtual" ? null : location.trim(),
            amount: amt,
            paymentRef: reference,
            paymentMethod: method,
        });
        setShowPay(false);
        onClose?.();
        onBooked?.(booking);
    };

    return (
        <div
            className="absolute inset-0 z-40 flex items-end"
            data-testid="booking-sheet"
            role="dialog"
            aria-modal="true"
        >
            <button
                onClick={() => onClose?.()}
                aria-label="Close"
                className="absolute inset-0 bg-black/55 animate-fade-in"
                data-testid="booking-sheet-scrim"
            />
            <div className="relative z-10 w-full max-h-[90vh] flex flex-col bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.4)] animate-slide-up pb-[max(20px,env(safe-area-inset-bottom))]">
                <div className="flex justify-center pt-2.5 pb-1 shrink-0">
                    <span className="h-1.5 w-10 rounded-full bg-muted" />
                </div>

                <div className="px-5 pt-1.5 pb-3 flex items-center justify-between border-b border-border/60 shrink-0">
                    <div className="min-w-0">
                        <h2 className="font-display text-[16px] font-semibold leading-tight">
                            Book this service
                        </h2>
                        <p className="text-[11.5px] text-muted-foreground inline-flex items-center gap-1.5 mt-0.5">
                            <ShieldCheck size={11} className="text-primary" />
                            Funds held in escrow until completion
                        </p>
                    </div>
                    <button
                        onClick={() => onClose?.()}
                        className="ml-2 h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted"
                        aria-label="Close"
                        data-testid="booking-sheet-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="overflow-y-auto no-scrollbar px-5 pt-4 pb-3 space-y-4">
                    {/* Service summary */}
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-muted/40 border border-border">
                        {service?.image && (
                            <img src={service.image} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-[14px] font-semibold truncate">
                                {service?.name}
                            </div>
                            <div className="text-[11.5px] text-muted-foreground truncate">
                                {service?.category} · ₦{amt.toLocaleString()}
                            </div>
                        </div>
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Date
                        </label>
                        <div className="flex items-center h-12 px-3.5 rounded-xl border border-border bg-secondary focus-within:bg-background focus-within:border-primary transition-colors">
                            <Calendar size={14} className="text-muted-foreground mr-2" />
                            <input
                                data-testid="booking-date-input"
                                type="date"
                                min={todayISO()}
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="flex-1 bg-transparent outline-none text-[14.5px]"
                            />
                        </div>
                    </div>

                    {/* Time */}
                    <div>
                        <label className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1 inline-flex items-center gap-1.5">
                            <Clock size={11} />
                            Time
                        </label>
                        <div className="grid grid-cols-4 gap-2" data-testid="booking-time-grid">
                            {TIME_SLOTS.map((t) => {
                                const active = time === t;
                                return (
                                    <button
                                        key={t}
                                        type="button"
                                        data-testid={`booking-time-${t}`}
                                        onClick={() => setTime(t)}
                                        className={[
                                            "h-10 rounded-xl text-[12.5px] font-medium border transition-colors active:scale-[0.97]",
                                            active
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-secondary text-foreground border-border hover:bg-muted",
                                        ].join(" ")}
                                    >
                                        {t}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Location / Virtual */}
                    <div>
                        <label className="block text-[11.5px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Mode
                        </label>
                        <div className="grid grid-cols-2 gap-2 mb-2" data-testid="booking-mode-tabs">
                            {allowInPerson && (
                                <button
                                    type="button"
                                    data-testid="booking-mode-in_person"
                                    onClick={() => setMode("in_person")}
                                    className={[
                                        "h-11 rounded-xl border inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium",
                                        mode === "in_person"
                                            ? "border-primary bg-primary/5 text-foreground"
                                            : "border-border bg-secondary text-muted-foreground hover:bg-muted",
                                    ].join(" ")}
                                >
                                    <MapPin size={13} />
                                    In-person
                                </button>
                            )}
                            {allowVirtual && (
                                <button
                                    type="button"
                                    data-testid="booking-mode-virtual"
                                    onClick={() => setMode("virtual")}
                                    className={[
                                        "h-11 rounded-xl border inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium",
                                        mode === "virtual"
                                            ? "border-primary bg-primary/5 text-foreground"
                                            : "border-border bg-secondary text-muted-foreground hover:bg-muted",
                                    ].join(" ")}
                                >
                                    <Globe size={13} />
                                    Virtual
                                </button>
                            )}
                        </div>
                        {mode === "in_person" ? (
                            <div className="flex items-center h-12 px-3.5 rounded-xl border border-border bg-secondary focus-within:bg-background focus-within:border-primary">
                                <MapPin size={14} className="text-muted-foreground mr-2" />
                                <input
                                    data-testid="booking-location-input"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="Address or landmark"
                                    className="flex-1 bg-transparent outline-none text-[14.5px]"
                                />
                            </div>
                        ) : (
                            <p className="text-[11.5px] text-muted-foreground px-1" data-testid="booking-virtual-note">
                                Virtual session — a meeting link will be shared by the provider.
                            </p>
                        )}
                    </div>

                    {/* Total */}
                    <div className="flex items-center justify-between pt-2 border-t border-border/60">
                        <span className="text-[12.5px] text-muted-foreground">Total</span>
                        <span className="font-display text-[17px] font-semibold" data-testid="booking-total">
                            ₦{amt.toLocaleString()}
                        </span>
                    </div>

                    {err && (
                        <p className="text-[12px] text-destructive" data-testid="booking-error">{err}</p>
                    )}
                </div>

                <div className="px-5 pt-3 shrink-0 border-t border-border/60">
                    <button
                        data-testid="booking-proceed-btn"
                        onClick={handleProceed}
                        disabled={!canProceed}
                        className="w-full py-3.5 rounded-2xl text-primary-foreground font-medium text-[14.5px] active:scale-[0.98] transition-transform disabled:opacity-50"
                        style={{
                            background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        Proceed to payment · ₦{amt.toLocaleString()}
                    </button>
                </div>
            </div>

            <PaymentSheet
                open={showPay}
                onClose={() => setShowPay(false)}
                onSuccess={handlePaymentSuccess}
                amount={amt}
                title="Confirm booking"
                purposeLabel="booking"
                customerEmail={user?.email}
                summary={
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-muted/40 border border-border">
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-[14px] font-semibold truncate">
                                {service?.name}
                            </div>
                            <div className="text-[11.5px] text-muted-foreground truncate">
                                {date} · {time} · {mode === "virtual" ? "Virtual" : location}
                            </div>
                        </div>
                    </div>
                }
                refundNote={
                    <p className="text-center text-[11.5px] text-muted-foreground inline-flex items-center justify-center gap-1.5 w-full">
                        <ShieldCheck size={11} className="text-primary" />
                        Funds held in escrow until you confirm completion
                    </p>
                }
            />
        </div>
    );
};

export default BookingSheet;
